import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/stages.css';
import { getProductBySlug } from '../data/products';
import { supplyChainStages, SupplyChainStage } from '../data/supplyChainStages';

const stageIcons: Record<SupplyChainStage['icon'], React.ReactNode> = {
  source: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="4" width="12" height="17" rx="2" />
      <rect x="9" y="2" width="6" height="3" rx="1" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  receive: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8l9-5 9 5-9 5-9-5z" />
      <path d="M3 8v8l9 5 9-5V8" />
      <path d="M12 13v8" />
    </svg>
  ),
  assets: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21s7-7.5 7-12a7 7 0 10-14 0c0 4.5 7 12 7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  ),
  dispense: (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="4" />
      <path d="M12 8v8M8 12h8" />
    </svg>
  ),
};

const StepArrow: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-border-strong)" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

interface SupplyChainStagesProps {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
}

const SupplyChainStages: React.FC<SupplyChainStagesProps> = ({
  eyebrow = 'The platform',
  title = 'One connected supply chain',
  subtitle = 'Four purpose-built apps that hand off to each other — from requesting a quote to dispensing the item at the point of use.',
}) => {
  return (
    <section className="solutions-section stage-section">
      <p className="hero-eyebrow stage-eyebrow">{eyebrow}</p>
      <h2 className="section-title">{title}</h2>
      <p className="section-subtitle">{subtitle}</p>

      <div className="stage-stepper" aria-hidden="true">
        {supplyChainStages.map((stage, i) => (
          <React.Fragment key={stage.slug}>
            <div className="stepper-node">{i + 1}</div>
            {i < supplyChainStages.length - 1 && (
              <div className="stepper-line">
                <span className="stepper-line-half" />
                <StepArrow />
                <span className="stepper-line-half" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="stage-grid">
        {supplyChainStages.map((stage) => {
          const product = getProductBySlug(stage.slug);
          return (
            <div key={stage.slug} className="card product-card stage-card">
              <div className="stage-icon">{stageIcons[stage.icon]}</div>
              <div className="stage-card-heading">
                <h3>{stage.title}</h3>
                <span className="stage-badge">Powered by {product?.name ?? stage.slug}</span>
              </div>
              <p className="stage-description">{stage.description}</p>
              <div className="product-card-actions">
                <Link to={`/product/${stage.slug}`} className="product-card-link">
                  Learn More
                </Link>
                {product?.appStoreUrl && (
                  <a
                    className="app-store-badge-link"
                    href={product.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Try ${product.name} yourself — download on the App Store`}
                  >
                    <img src="/images/icons/badge-appstore.png" alt="Download on the App Store" />
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SupplyChainStages;
